import { CellSpacing } from '@cbhq/cds-common';
import { Button } from '@cbhq/cds-web/buttons';
import { Box, Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import { Tooltip } from '@cbhq/cds-web/overlays/Tooltip/Tooltip';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TableCellFallback } from '@cbhq/cds-web/tables/TableCellFallback';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Link, TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { MAX_MEMBER_INVITE_LIMIT, useProject } from ':cloud/contexts/ProjectContext';
import { useGetProjects } from ':cloud/queries/Projects/useGetProjects';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import {
  ProjectInviteNEW,
  ProjectRoleNEW,
  ProjectTeamMember,
  ProjectUserDetailsNEW,
  ProjectWithRole,
} from ':cloud/types/service_proto';
import { formatRole, getDisplayName } from ':cloud/utils/formatters/naming';
import { ROLES_DOCS_LINK } from ':cloud/utils/links';
import { ActionCell } from ':cloud/widgets/access/components/ActionCellNEW';
import { StatusCell } from ':cloud/widgets/access/components/StatusCellNEW';
import Permission from ':cloud/widgets/auth';
import { OWNER_ADMIN_ROLES } from ':cloud/widgets/auth/util';

export const inviteWarning = `Maximum of ${MAX_MEMBER_INVITE_LIMIT.toString()} active invitations has been reached`;
const exchangeWarning = 'Your organization can only have 1 user since you are an Exchange user';

export function isProjectInvite(member: ProjectTeamMember): member is ProjectInviteNEW {
  return 'accepted' in member;
}

interface InviteButtonProps {
  hasInvitedMaxUsers: boolean;
  toggleInviteModalOn: () => void;
  exchangeEnabled: boolean;
}
function InviteButton({
  hasInvitedMaxUsers,
  toggleInviteModalOn,
  exchangeEnabled,
}: InviteButtonProps) {
  if (exchangeEnabled) {
    return (
      <Tooltip content={exchangeWarning} placement="bottom">
        <Button disabled onPress={toggleInviteModalOn}>
          Invite member
        </Button>
      </Tooltip>
    );
  }
  if (hasInvitedMaxUsers) {
    return (
      <Tooltip content={inviteWarning} placement="bottom">
        <Button disabled onPress={toggleInviteModalOn}>
          Invite member
        </Button>
      </Tooltip>
    );
  }
  return (
    <Button disabled={hasInvitedMaxUsers} onPress={toggleInviteModalOn}>
      Invite member
    </Button>
  );
}

function AccessTableBodyFallback() {
  const tableRowInnerSpacing: CellSpacing = { spacingVertical: 2 };

  return (
    <>
      {Array.from({ length: 4 }, () => (
        <TableRow>
          <TableCellFallback title start="avatar" innerSpacing={tableRowInnerSpacing} />
          <TableCellFallback title innerSpacing={tableRowInnerSpacing} />
          <TableCellFallback title innerSpacing={tableRowInnerSpacing} />
          <TableCellFallback end="pictogram" innerSpacing={tableRowInnerSpacing} />
        </TableRow>
      ))}
    </>
  );
}

export interface AccessTableWrapperProps {
  toggleInviteModalOn: () => void;
  handleMemberAction: (event: string, user: ProjectUserDetailsNEW) => void;
}

export function AccessTableWrapperNEW({
  toggleInviteModalOn,
  handleMemberAction,
}: AccessTableWrapperProps) {
  const { user, activeOrg } = useGetUser();
  const { isLoading: isProjectsLoading } = useGetProjects(activeOrg?.organizationId);

  // Remove type cast after project based team management is fully rolled out - useIsProjectTeamsEnabled
  const { hasInvitedMaxUsers, selectedProject } = useProject() as {
    hasInvitedMaxUsers: boolean;
    selectedProject: ProjectWithRole | null;
  };

  const users = selectedProject?.users || [];
  const invites = selectedProject?.invites || [];

  const allUsers = [...users, ...invites];

  const renderAccessTableContents = allUsers
    .filter((member: ProjectTeamMember) => {
      return member.role !== ProjectRoleNEW.Unspecified;
    })
    .map((member: ProjectTeamMember) => {
      const signedInUser = user.email === member.email;
      const isInvite = isProjectInvite(member);

      return (
        <TableRow key={`row--${member.email}`}>
          <TableCell
            start={
              <Avatar
                alt={member.email}
                size="xl"
                selected={signedInUser}
                dangerouslySetSize={34}
                src={isInvite ? undefined : member.avatarUrl}
              />
            }
          >
            {!isInvite && <TextHeadline as="p">{getDisplayName(member)}</TextHeadline>}
            <TextBody color="foregroundMuted" as="p">
              {member.email}
            </TextBody>
          </TableCell>
          <TableCell direction="horizontal" justifyContent="flex-start">
            <TextHeadline spacingEnd={2} spacingVertical={2} as="span">
              {formatRole(member.role)}
            </TextHeadline>
            {signedInUser ? <Tag colorScheme="blue">YOU</Tag> : null}
          </TableCell>
          <Permission permission={OWNER_ADMIN_ROLES} fallback={<StatusCell status={!isInvite} />}>
            <>
              <StatusCell manageUsers status={!isInvite} />
              <ActionCell
                teamMember={member}
                signedInUser={signedInUser}
                handleMemberAction={handleMemberAction}
              />
            </>
          </Permission>
        </TableRow>
      );
    });

  return (
    <VStack>
      <HStack width="100%" justifyContent="space-between" spacingHorizontal={7} spacingVertical={5}>
        <VStack width="66%" gap={1}>
          <TextTitle3 as="h3">Project team</TextTitle3>
          <TextBody as="p" color="foregroundMuted">
            A list of people who can access this project, including their roles. For details, see{' '}
            <Link href={ROLES_DOCS_LINK} openInNewWindow underline={false}>
              Roles and Permissions
            </Link>
          </TextBody>
        </VStack>
        <Permission permission={OWNER_ADMIN_ROLES}>
          <VStack justifyContent="center">
            <InviteButton
              hasInvitedMaxUsers={hasInvitedMaxUsers}
              {...{
                toggleInviteModalOn,
                exchangeEnabled: user.exchangeEnabled,
              }}
            />
          </VStack>
        </Permission>
      </HStack>
      <Divider />
      <Box spacingHorizontal={5} spacingVertical={5}>
        <Table variant="default" tableLayout="auto">
          <TableHeader>
            <TableRow>
              <TableCell title="Name" />
              <TableCell title="Role" />
              <Permission
                permission={OWNER_ADMIN_ROLES}
                fallback={<TableCell title="Status" alignItems="flex-end" />}
              >
                <TableCell title="Status" />
              </Permission>
              <Permission permission={OWNER_ADMIN_ROLES}>
                <TableCell title="Action" alignItems="flex-end" />
              </Permission>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isProjectsLoading ? <AccessTableBodyFallback /> : renderAccessTableContents}
          </TableBody>
        </Table>
      </Box>
    </VStack>
  );
}
